import React, { useState } from "react";
import { PageShell, EditableSection } from "./PageShell";
import { Mail, MapPin, Phone, Send } from "lucide-react";
import { useSiteContent } from "@/lib/siteContent";
import { toast } from "sonner";

const PROMISES = [{
  heading: "How we respond",
  body: "Every message is answered by a member of our team — not a bot. Concierge and general enquiries are typically answered within 4 business hours. Wholesale and press enquiries within 1–2 business days.",
}];

export default function ContactPage() {
  const { site_settings: s } = useSiteContent();
  const [form, setForm] = useState({ name: "", email: "", subject: "General", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    const subj = encodeURIComponent(`[Scentodore] ${form.subject} — ${form.name}`);
    const body = encodeURIComponent(`${form.message}\n\n— ${form.name} (${form.email})`);
    window.location.href = `mailto:${s.email || "hello@scentodore.com"}?subject=${subj}&body=${body}`;
    setTimeout(() => {
      toast.success("Your mail client has opened — send when ready.");
      setSubmitting(false);
    }, 300);
  };

  return (
    <PageShell
      slug="contact"
      testid="contact"
      eyebrow="Contact"
      title="We reply personally."
      intro="Concierge, wholesale, press, custom commissions — write to us and a real member of the Scentodore team will respond, always."
    >
      <div className="grid lg:grid-cols-[1.2fr_1fr] gap-10 mb-14">
        <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-4 max-w-2xl">
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs uppercase tracking-[0.16em] text-[#1A1918]/70">Your name</span>
              <input required name="name" value={form.name} onChange={onChange} data-testid="contact-name" className="mt-1 w-full px-4 py-3 rounded-xl border border-black/10 bg-[#FAF8F5] text-sm focus:outline-none focus:border-[#C5A059]" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-[0.16em] text-[#1A1918]/70">Email</span>
              <input required type="email" name="email" value={form.email} onChange={onChange} data-testid="contact-email" className="mt-1 w-full px-4 py-3 rounded-xl border border-black/10 bg-[#FAF8F5] text-sm focus:outline-none focus:border-[#C5A059]" />
            </label>
          </div>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.16em] text-[#1A1918]/70">Reason for contact</span>
            <select name="subject" value={form.subject} onChange={onChange} data-testid="contact-subject" className="mt-1 w-full px-4 py-3 rounded-xl border border-black/10 bg-[#FAF8F5] text-sm focus:outline-none focus:border-[#C5A059]">
              <option>General</option>
              <option>Order Support</option>
              <option>Return or Exchange</option>
              <option>Wholesale / B2B</option>
              <option>Press & Media</option>
              <option>Custom Engraving / Gifting</option>
              <option>Feedback</option>
            </select>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.16em] text-[#1A1918]/70">Message</span>
            <textarea required name="message" value={form.message} onChange={onChange} rows={6} data-testid="contact-message" placeholder="If this is about an order, please include your order number." className="mt-1 w-full px-4 py-3 rounded-xl border border-black/10 bg-[#FAF8F5] text-sm focus:outline-none focus:border-[#C5A059] resize-none" />
          </label>
          <button type="submit" disabled={submitting} data-testid="contact-submit" className="inline-flex items-center gap-2 bg-[#1A1918] text-[#FAF8F5] px-6 py-3.5 rounded-xl text-xs uppercase tracking-[0.18em] hover:bg-[#C5A059] hover:text-[#0B0A08] transition-colors disabled:opacity-60">
            {submitting ? "Opening mail…" : "Send Message"} <Send className="h-4 w-4" />
          </button>
        </form>

        <aside className="space-y-6">
          <div className="p-6 rounded-2xl border border-black/10 bg-[#F1ECE4]/40">
            <div className="font-serif text-lg text-[#1A1918] mb-4">Reach us directly</div>
            <ul className="space-y-3 text-[14px] text-[#1A1918]/80">
              {s.email ? (
                <li className="flex items-start gap-2.5"><Mail className="h-4 w-4 mt-0.5 text-[#C5A059]" /><a href={`mailto:${s.email}`} className="hover:text-[#C5A059] break-all" data-testid="contact-direct-email">{s.email}</a></li>
              ) : null}
              {s.phone ? (
                <li className="flex items-start gap-2.5"><Phone className="h-4 w-4 mt-0.5 text-[#C5A059]" /><a href={`tel:${(s.phone || "").replace(/[^\d+]/g, "")}`} className="hover:text-[#C5A059]" data-testid="contact-direct-phone">{s.phone}</a></li>
              ) : null}
              {s.address ? (
                <li className="flex items-start gap-2.5"><MapPin className="h-4 w-4 mt-0.5 text-[#C5A059]" /><span>{s.address}</span></li>
              ) : null}
            </ul>
          </div>
          <div className="p-6 rounded-2xl border border-black/10 bg-[#FAF8F5]">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#C5A059] mb-1.5">Response Times</div>
            <div className="text-[14px] text-[#1A1918]/80 leading-[1.75]">
              Concierge & general — within 4 business hours<br />Wholesale & press — within 1–2 business days
            </div>
          </div>
        </aside>
      </div>

      <EditableSection slug="contact" sectionKey="promises" fallback={PROMISES} />
    </PageShell>
  );
}
